import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Target, Zap, CheckCircle, BarChart3 } from "lucide-react";
import { trackEvent } from "@/App";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ onReset }) {
  const navigate = useNavigate();
  const [scanCount, setScanCount] = useState(null);

  useEffect(() => {
    trackEvent("page_view", "landing");
    axios.get(`${API}/scan-count`).then(r => setScanCount(r.data.count)).catch(() => {});
  }, []);

  const handleStart = () => { onReset(); navigate("/capture"); };

  return (
    <div data-testid="landing-page" className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Ambient gradient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-80 h-80 rounded-full bg-[#e91e63] opacity-[0.04] blur-[120px]" style={{animation:"pulseOrb 8s ease-in-out infinite"}} />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full bg-[#2196f3] opacity-[0.04] blur-[120px]" style={{animation:"pulseOrb 10s ease-in-out infinite reverse"}} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 rounded-full bg-[#ff9f1c] opacity-[0.03] blur-[100px]" style={{animation:"colorShift 12s ease-in-out infinite"}} />
      </div>

      <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center space-y-10">
        {/* Dental scan icon with scanning animation */}
        <div className="animate-fade-in-up opacity-0">
          <div className="relative w-32 h-32 flex items-center justify-center logo-container">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                {/* Gradient for outer corners - purple to blue at top */}
                <linearGradient id="cornerGradientTop" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8B5CF6"/>
                  <stop offset="100%" stopColor="#6366F1"/>
                </linearGradient>
                {/* Gradient for outer corners - orange to red at bottom */}
                <linearGradient id="cornerGradientBottom" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F97316"/>
                  <stop offset="100%" stopColor="#EF4444"/>
                </linearGradient>
                {/* Gradient for inner frame and tooth - magenta/pink */}
                <linearGradient id="innerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#A855F7"/>
                  <stop offset="50%" stopColor="#EC4899"/>
                  <stop offset="100%" stopColor="#F43F5E"/>
                </linearGradient>
              </defs>
              
              {/* Outer corner brackets */}
              <path d="M5 22 L5 5 L22 5" fill="none" stroke="url(#cornerGradientTop)" strokeWidth="4" strokeLinecap="round"/>
              <path d="M78 5 L95 5 L95 22" fill="none" stroke="url(#cornerGradientTop)" strokeWidth="4" strokeLinecap="round"/>
              <path d="M5 78 L5 95 L22 95" fill="none" stroke="url(#cornerGradientBottom)" strokeWidth="4" strokeLinecap="round"/>
              <path d="M78 95 L95 95 L95 78" fill="none" stroke="url(#cornerGradientBottom)" strokeWidth="4" strokeLinecap="round"/>
              
              {/* Inner rounded rectangle frame */}
              <rect x="22" y="22" width="56" height="56" rx="4" fill="none" stroke="url(#innerGradient)" strokeWidth="2.5"/>
              
              {/* Tooth icon - proper molar shape with two roots */}
              <path d="M50 28 
                       C44 28 41 30 39 33 
                       C37 36 36 39 36 42
                       C36 46 37 48 38 50
                       L38 50
                       C38 54 38 58 40 62
                       C41 65 42 68 42 70
                       C42 72 44 72 45 70
                       C46 67 46 64 47 61
                       C47.5 59 48.5 58 50 58
                       C51.5 58 52.5 59 53 61
                       C54 64 54 67 55 70
                       C56 72 58 72 58 70
                       C58 68 59 65 60 62
                       C62 58 62 54 62 50
                       L62 50
                       C63 48 64 46 64 42
                       C64 39 63 36 61 33
                       C59 30 56 28 50 28 Z" 
                    fill="none" stroke="url(#innerGradient)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              
              {/* Horizontal scan line - animated */}
              <line x1="26" y1="50" x2="74" y2="50" stroke="url(#innerGradient)" strokeWidth="2" strokeLinecap="round" className="scan-line-svg"/>
            </svg>
            {/* Scanning light effect */}
            <div className="scan-light"></div>
          </div>
        </div>

        <div className="space-y-4 animate-fade-in-up opacity-0 stagger-1">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Smile<span className="bg-gradient-to-r from-[#ff9f1c] via-[#e91e63] to-[#2196f3] bg-clip-text text-transparent">Sense</span>
          </h1>
          <p className="text-base text-[#7a7894] max-w-sm mx-auto leading-relaxed">
            Laboratory-grade shade matching in your pocket. Calibrated for your clinic, ready in seconds.
          </p>
        </div>

        <div className="w-full animate-fade-in-up opacity-0 stagger-2">
          <Button data-testid="start-new-match-btn" onClick={handleStart}
            className="btn-gradient w-full h-14 text-base font-semibold rounded-2xl text-white border-0 shadow-lg shadow-[#e91e63]/20">
            <Camera className="w-5 h-5 mr-2" />Start New Shade Selection
          </Button>
        </div>

        {scanCount !== null && scanCount > 0 && (
          <div className="animate-fade-in-up opacity-0 stagger-2">
            <Badge data-testid="scan-counter-badge" variant="outline"
              className="rounded-full px-5 py-2 text-sm border-[#33304a] text-[#e91e63] bg-[#232134]/80">
              <BarChart3 className="w-3.5 h-3.5 mr-2" />{scanCount.toLocaleString()} scans performed
            </Badge>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 animate-fade-in-up opacity-0 stagger-3">
          {[
            { icon: Target, label: "Lab Grade Accuracy" },
            { icon: Zap, label: "Fast Under 7s" },
            { icon: CheckCircle, label: "No Guessing" },
          ].map(({ icon: Icon, label }) => (
            <Badge key={label} variant="outline"
              className="rounded-full px-4 py-1.5 text-xs border-[#33304a] text-[#7a7894] bg-[#232134]">
              <Icon className="w-3 h-3 mr-1.5" />{label}
            </Badge>
          ))}
        </div>

        <div className="w-full space-y-5 animate-fade-in-up opacity-0 stagger-4">
          <p className="text-xs font-medium text-[#5a5873] uppercase tracking-widest">How it works</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { step: "01", label: "Capture", desc: "Snap a photo of the target tooth alongside any standard VITA™ shade tab." },
              { step: "02", label: "Select", desc: "Identify the tooth and reference tab. Our engine automatically neutralizes ambient lighting noise." },
              { step: "03", label: "Match", desc: "Receive an objective shade match based on perceptual color science, not subjective guesswork." },
            ].map((item) => (
              <div key={item.step} data-testid={`step-${item.step}`} className="flex flex-col items-center gap-2 px-2">
                <span className="text-2xl font-bold bg-gradient-to-r from-[#ff9f1c] to-[#e91e63] bg-clip-text text-transparent">{item.step}</span>
                <span className="text-sm font-semibold text-white">{item.label}</span>
                <span className="text-xs text-[#7a7894] leading-relaxed">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#3d3b55] animate-fade-in-up opacity-0 stagger-5">VITA™ Classical shade guide</p>
      </div>
    </div>
  );
}
